<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  useCustomFlowsQuery,
  type CustomFlowsQuery,
} from "@/graphql/operations";
import { Dropdown, FlashMessage, LoadingSpinner } from "@/components";
import DeleteCustomFlowModal from "./DeleteCustomFlowModal.vue";
import CreateCustomFlowModal from "./CreateCustomFlowModal.vue";
import DuplicateCustomFlowModal from "./DuplicateCustomFlowModal.vue";
import { userData } from "@/init";

const router = useRouter();
const { t } = useI18n({});

const emits = defineEmits(["contentLoaded"]);

const flashMessageActive = ref<boolean>(false);
const flashMessageText = ref<string>("");
const flashMessageState = ref<"success" | "error">("success");
let flashMessageTimer: number;

function showFlashMessage(
  text: string,
  state: "success" | "error" = "success",
) {
  clearTimeout(flashMessageTimer);
  flashMessageText.value = text;
  flashMessageState.value = state;
  flashMessageActive.value = true;

  flashMessageTimer = window.setTimeout(() => {
    flashMessageActive.value = false;
  }, 3000);
}

const creatorId = computed(() => userData.value?.postgresId ?? null);

const { data, fetching, error, operation, executeQuery } = useCustomFlowsQuery({
  variables: computed(() => ({ creatorId: creatorId.value ?? "0" })),
  pause: computed(() => !creatorId.value),
  requestPolicy: "cache-and-network",
});

watch(
  data,
  (newData, oldData) => {
    if (newData && !oldData) {
      emits("contentLoaded");
    }
  },
  { immediate: true },
);

// Keep the last successful result through a failed refresh, scoped to its owner.
const lastSuccessfulFlows = shallowRef<{
  creatorId: string;
  nodes: NonNullable<CustomFlowsQuery["flows"]>["nodes"];
}>();
watch(
  [data, operation],
  ([result, query]) => {
    if (result?.flows && query?.variables?.creatorId === creatorId.value) {
      lastSuccessfulFlows.value = {
        creatorId: creatorId.value!,
        nodes: result.flows.nodes,
      };
    }
  },
  { immediate: true },
);
const flows = computed(() =>
  lastSuccessfulFlows.value?.creatorId === creatorId.value
    ? lastSuccessfulFlows.value?.nodes
    : undefined,
);
const refreshFlows = () => {
  if (creatorId.value) executeQuery({ requestPolicy: "network-only" });
};

const contextMenuItems = [
  { value: "duplicate", title: t("customFlows.duplicate") },
  {
    value: "delete",
    title: t("customFlows.delete"),
    destructive: true,
  },
];

const showDeleteModal = ref<boolean>(false);
const flowToDelete = ref<{ id: string; title: string } | null>(null);
const displayDeleteConfirmation = (id: string) => {
  const flow = flows.value?.find((flow) => flow?.id === id);
  if (!flow) return;
  flowToDelete.value = { id: flow.id, title: flow.title };
  showDeleteModal.value = true;
};
const closeDeleteModal = () => {
  showDeleteModal.value = false;
  flowToDelete.value = null;
};

const handleAction = (flowId: string, action: string) => {
  if (action === "delete") {
    displayDeleteConfirmation(flowId);
  } else if (action === "duplicate") {
    displayDuplicateModal(flowId);
  }
};

const showDuplicateModal = ref<boolean>(false);
const flowToDuplicate = ref<{ id: string; title: string } | null>(null);
const displayDuplicateModal = (flowId: string) => {
  const flow = flows.value?.find((flow) => flow!.id === flowId);
  if (!flow) {
    return;
  }

  flowToDuplicate.value = { id: flow.id, title: flow.title };
  showDuplicateModal.value = true;
};

const closeDuplicateModal = () => {
  showDuplicateModal.value = false;
  flowToDuplicate.value = null;
};

const handleFlowDuplicated = () => {
  closeDuplicateModal();
  refreshFlows();
  showFlashMessage(t("customFlows.duplicateSuccess"));
};

const handleDuplicateError = () => {
  closeDuplicateModal();
  showFlashMessage(t("customFlows.duplicateError"), "error");
};

const handleFlowDeleted = () => {
  closeDeleteModal();
  refreshFlows();
  showFlashMessage(t("customFlows.deleteConfirmation.success"));
};

const showCreateModal = ref<boolean>(false);
const displayCreateModal = () => {
  showCreateModal.value = true;
};

const redirect = (id: string) => {
  router.push({ name: "CustomFlow", params: { id: id } });
};

const intervalId = window.setInterval(refreshFlows, 5000);
onBeforeUnmount(() => {
  window.clearTimeout(flashMessageTimer);
  if (intervalId) {
    window.clearInterval(intervalId);
  }
});
</script>

<template>
  <flash-message v-if="flashMessageActive" :state="flashMessageState">
    {{ flashMessageText }}
  </flash-message>

  <div class="pane page_content" :class="{ has_flows: flows?.length }">
    <div class="title_container">
      <h3>{{ $t("customFlows.title") }}</h3>

      <div class="control_container">
        <button
          v-if="flows && flows.length > 0"
          class="button primary"
          @click="displayCreateModal"
          data-test="create-flow-ctrl"
        >
          <span class="material-symbols-outlined">add</span>
          {{ $t("customFlows.createNew") }}
        </button>
      </div>
    </div>

    <div v-if="error" class="message error list_error" role="alert">
      {{ $t("error") }}
      <button class="button" :disabled="fetching" @click="refreshFlows">
        {{ $t("documentPresenter.tryAgain") }}
      </button>
    </div>

    <div v-if="!creatorId || (!flows && fetching)" class="loading_status">
      <LoadingSpinner :color="'var(--classic-text, black)'" />
    </div>

    <div
      v-else-if="!error && (!flows || flows.length === 0)"
      class="empty_pane"
    >
      <div>
        <span class="material-symbols-outlined"> post_add </span>

        <h2>{{ $t("customFlows.emptyState.title") }}</h2>
        <p class="info">
          {{ $t("customFlows.emptyState.info") }}
        </p>
        <button
          class="button primary"
          @click="displayCreateModal"
          data-test="create-flow-ctrl"
        >
          {{ $t("customFlows.createNew") }}
        </button>
      </div>
    </div>

    <div v-else-if="flows && flows.length > 0">
      <table class="data_table clickable">
        <thead>
          <tr>
            <th>{{ $t("customFlows.columns.title") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="flow in flows" :key="flow!.id">
            <td>
              <router-link
                :to="{ name: 'CustomFlow', params: { id: flow!.id } }"
              >
                {{ flow!.title }}
              </router-link>
            </td>

            <td>
              <Dropdown
                class="right"
                fit-viewport
                :trigger-aria-label="flow!.title"
                :items="contextMenuItems"
                @change="(action: string) => handleAction(flow!.id, action)"
              >
                <span class="material-symbols-outlined">more_vert</span>
              </Dropdown>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="showDeleteModal && flowToDelete">
      <delete-custom-flow-modal
        :active="showDeleteModal"
        :flowId="flowToDelete.id"
        :title="flowToDelete.title"
        @close="closeDeleteModal"
        @success="handleFlowDeleted"
      />
    </template>

    <template v-if="showCreateModal">
      <create-custom-flow-modal
        :active="showCreateModal"
        @close="showCreateModal = false"
        @success="redirect"
      />
    </template>

    <template v-if="showDuplicateModal && flowToDuplicate">
      <duplicate-custom-flow-modal
        :active="showDuplicateModal"
        :flow-id="flowToDuplicate.id"
        :flow-title="flowToDuplicate.title"
        @close="closeDuplicateModal"
        @success="handleFlowDuplicated"
        @error="handleDuplicateError"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
button {
  font-size: 0.9em;
}

table.data_table thead th:first-child {
  padding-left: 0;
}

.pane {
  height: 100%;

  &.has_flows {
    box-sizing: border-box;
    height: auto;
    min-height: 100%;
  }

  .page_content {
    height: 100%;
  }
}

.pane :deep(.button:focus-visible) {
  outline: 2px solid var(--classic-focus, #3273dc);
  outline-offset: 2px;
}

.list_error {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.loading_status {
  height: calc(100% - 75px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.title_container {
  display: flex;

  h3 {
    font-size: 1.1rem;
    font-weight: 500;
    flex: 1;
  }

  .control_container {
    display: flex;
    align-items: center;

    .button {
      margin-left: 1.5em;
    }
  }
}

table {
  &.data_table {
    table-layout: fixed;
    box-shadow: none;
  }

  a {
    color: var(--classic-text, #444);
    font-weight: 500;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  thead tr th:last-child,
  tbody tr td:last-child {
    width: 30px;
  }

  td .material-symbols-outlined {
    font-size: 1.3em;
    color: var(--classic-accent, #34b5ba);
    border: 1px solid var(--classic-accent, #34b5ba);
    border-radius: 50%;
    padding: 0.2em;
  }
}

td a {
  display: block;
  overflow-wrap: anywhere;
}

.material-symbols-outlined {
  vertical-align: middle;
}

.empty_pane {
  height: calc(100% - 75px);
  display: flex;
  justify-content: center;

  .material-symbols-outlined {
    font-size: 6em;
    opacity: 0.4;
  }

  h2 {
    margin-bottom: 0;
  }

  p {
    margin: 0.6em 0 1.5em 0;
  }

  > div {
    flex: none;
    text-align: center;
    margin-top: 5em;
  }
}
</style>
